import React from "react";
import {
  DateField,
  FunctionField,
  List,
  NumberField,
  TextField as RaTextField,
  useSidebarState,
  useTranslate,
} from "react-admin";
import { useSearchParams } from "react-router-dom";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import {
  CustomDatagrid,
  FilterAutocomplete,
  ListActionsBar,
} from "@/components/common";
import { getAccountsList } from "@/features/manage-groups/services/groupService";
import { getAllCostCenters } from "@/features/accounts/services/accountService";
import CreditTransactionExportButtons from "./CreditTransactionExportButtons";

const i18nBase = "resources.credit-consumption/credit-transaction";

// Catalogue from the backend (CreditTransaction.TransactionType); not every
// value appears in every environment.
const TRANSACTION_TYPES = [
  "CREDIT_PURCHASE",
  "TOPUP",
  "PLAN",
  "RESERVE",
  "DEDUCT",
  "REFUND",
  "MANUAL_ADJ",
];

const ListActions: React.FC = () => {
  const translate = useTranslate();
  const [accountNames, setAccountNames] = React.useState<string[]>([]);
  const [costCenterNames, setCostCenterNames] = React.useState<string[]>([]);

  React.useEffect(() => {
    let cancelled = false;
    void getAccountsList().then((accounts) => {
      if (!cancelled)
        setAccountNames(accounts.map((a) => a.name).filter(Boolean));
    });
    void getAllCostCenters().then((ccs) => {
      if (!cancelled) setCostCenterNames(ccs.map((c) => c.name).filter(Boolean));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <ListActionsBar
      filters={
        <>
          <FilterAutocomplete
            source="accountName"
            label={translate(`${i18nBase}.filters.account`, { _: "Account" })}
            options={accountNames}
          />
          <FilterAutocomplete
            source="costCenterName"
            label={translate(`${i18nBase}.filters.costCenterName`)}
            options={costCenterNames}
          />
          <FilterAutocomplete
            source="transactionType"
            label={translate(`${i18nBase}.filters.transactionType`)}
            options={TRANSACTION_TYPES}
          />
        </>
      }
      addButton={<CreditTransactionExportButtons />}
    />
  );
};

export const CreditTransactionList: React.FC = () => {
  const translate = useTranslate();
  const [sidebarOpen] = useSidebarState();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const maxWidth = isMobile ? "100%" : sidebarOpen ? "85vw" : "95vw";

  // Pre-filter when arriving from an account's "Credit transactions" action.
  // The list disables URL sync, so seed the filter via filterDefaultValues.
  const [searchParams] = useSearchParams();
  const initialFilters = React.useMemo(() => {
    const f: Record<string, string> = {};
    const accountName = searchParams.get("accountName");
    const costCenterName = searchParams.get("costCenterName");
    if (accountName) f.accountName = accountName;
    if (costCenterName) f.costCenterName = costCenterName;
    return f;
  }, [searchParams]);

  return (
    <Box>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          color: "text.primary",
          mb: 1,
          mt: 0,
          ml: 2,
        }}
      >
        {translate(`${i18nBase}.name`, { smart_count: 2 })}
      </Typography>
      <List
        actions={<ListActions />}
        sort={{ field: "createdAt", order: "DESC" }}
        storeKey={false}
        disableSyncWithLocation
        filterDefaultValues={initialFilters}
        perPage={10}
        exporter={false}
        sx={{
          maxWidth,
          overflow: "visible",
          "& .RaList-main, & .RaList-content": {
            overflow: "visible",
            width: "100%",
            maxWidth: "100%",
          },
          "& .RaListToolbar-root, & .RaList-actions": {
            display: "flex",
            width: "100%",
            mb: { xs: 1, sm: 2 },
            px: 0,
            py: { xs: 0, sm: 1 },
            minHeight: "auto",
          },
        }}
      >
        <CustomDatagrid
          primarySource="costCenterName"
          bulkActionButtons={false}
          disableMobileView
        >
          <FunctionField
            source="accountName"
            label={translate(`${i18nBase}.fields.account`, { _: "Account" })}
            render={(record) =>
              (record as { accountName?: string }).accountName ?? "—"
            }
          />
          <RaTextField source="costCenterName" />
          <RaTextField source="transactionType" />
          <NumberField source="amount" />
          <NumberField source="balanceAfter" />
          <DateField
            source="createdAt"
            showTime
            options={{
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            }}
          />
          <RaTextField source="createdByUserName" />
        </CustomDatagrid>
      </List>
    </Box>
  );
};

export default CreditTransactionList;
